import React from 'react'
import { Link } from 'react-router-dom'

import { STATUS_META, fmtDate, daysBetween, daysLeft, isExpiringSoon } from './helpers/trial_data'

const ROW_ACTIONS = {
  active: [
    { mode: 'extend', icon: 'fa-calendar-plus', label: 'Extend' },
    { mode: 'expire', icon: 'fa-circle-stop', label: 'Expire (force stop)' },
  ],
  expired: [
    { mode: 'reenable', icon: 'fa-rotate-right', label: 'Re-enable (force start)' },
  ],
}

// Whole-day count remaining: prefer the server value, else compute from the end date.
const remainingDays = (trial) => (trial.daysRemaining != null ? trial.daysRemaining : daysLeft(trial.endDate))

const StatusDot = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.active
  return (
    <span className='tp-status-dot'>
      <span
        className='dot'
        style={{ background: meta.color }}
      />
      {meta.label}
    </span>
  )
}

const UserCell = ({ trial, client }) => {
  const userLink = `/${client}/individual_user_info?user_id=${trial.co_user_id}`
  return (
    <div>
      <Link
        className='tp-user-name tp-user-link'
        to={userLink}
      >
        {trial.name}
      </Link>
      <div className='tp-user-sub'>{trial.email}</div>
      {trial.org && (
        <div className='tp-user-sub tp-user-org'>
          {trial.org_id ? (
            <Link
              className='tp-user-link'
              to={`/${client}/organization_info?org_id=${trial.org_id}`}
            >
              {trial.org}
            </Link>
          ) : (
            trial.org
          )}
        </div>
      )}
    </div>
  )
}

const EndDateCell = ({ trial }) => {
  const dl = remainingDays(trial)
  let sub = null
  if (trial.status === 'active') {
    sub = isExpiringSoon(trial)
      ? (
        <span className='tp-end-sub tp-end-sub--soon'>
          {dl <= 0 ? 'Expires today' : 'Expires in ' + dl + ' day' + (dl === 1 ? '' : 's')}
        </span>
      )
      : <span className='tp-end-sub'>{dl} days left</span>
  } else if (trial.status === 'expired') {
    sub = <span className='tp-end-sub'>{Math.abs(daysLeft(trial.endDate))} days ago</span>
  }
  return (
    <div>
      <div>{fmtDate(trial.endDate)}</div>
      {sub}
    </div>
  )
}

const RowActions = ({ trial, onAction }) => {
  const acts = ROW_ACTIONS[trial.status] || []
  if (acts.length === 0) {
    return <span className='tp-row-empty'>&mdash;</span>
  }
  return (
    <div className='tp-row-actions'>
      {acts.map((a) => (
        <button
          key={a.mode}
          type='button'
          className='tp-icon-btn'
          title={a.label}
          aria-label={a.label}
          onClick={() => onAction(a.mode, trial)}
        >
          <i className={'fa-solid ' + a.icon} />
        </button>
      ))}
    </div>
  )
}

const TrialPeriodsTable = ({ rows, client, onAction }) => {
  return (
    <div className='tp-table-wrap'>
      <div className='tp-table-scroll'>
        <table className='tp-table'>
          <thead>
            <tr>
              <th>User / Organization</th>
              <th className='tp-right'>Duration</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Offered By</th>
              <th className='tp-right' />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  className='tp-empty'
                  colSpan={7}
                >
                  No Trials Found
                </td>
              </tr>
            ) : (
              rows.map((trial) => (
                <tr key={trial.co_user_id}>
                  <td>
                    <UserCell
                      trial={trial}
                      client={client}
                    />
                  </td>
                  <td className='tp-right'>
                    <span className='tp-tabular'>{trial.durationDays != null ? trial.durationDays : daysBetween(trial.startDate, trial.endDate)} days</span>
                  </td>
                  <td>
                    <span className='tp-tabular'>{fmtDate(trial.startDate)}</span>
                  </td>
                  <td>
                    <EndDateCell trial={trial} />
                  </td>
                  <td>
                    <div className='tp-status-cell'>
                      <StatusDot status={trial.status} />
                      {isExpiringSoon(trial) && <span className='tp-soon-tag'>Soon</span>}
                    </div>
                  </td>
                  <td>
                    <div>{trial.offeredBy}</div>
                    <div className='tp-user-sub'>{fmtDate(trial.offeredDate)}</div>
                  </td>
                  <td className='tp-right'>
                    <RowActions
                      trial={trial}
                      onAction={onAction}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TrialPeriodsTable
